/* eslint-disable class-methods-use-this */
/* eslint-disable camelcase */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import {
  logAccess,
  ACCESS_TYPE_NORMAL,
  ACCESS_TYPE_EXTENDED,
  ACCESS_TYPE_PAYWALL,
} from 'actions/logging';
import { CONTENT_TYPES } from '../../constants';

const INITIAL_ACCESS_DELAY = 3000;
const EXTENDED_ACCESS_DELAY = 30000;

function withUserAccessLogger(contentType, WrappedComponent) {
  class UserAccessLogger extends Component {
    static propTypes = {
      loading: PropTypes.bool,
      checkingAccess: PropTypes.bool,
      logAccess: PropTypes.func.isRequired,
      clientId: PropTypes.string,
      recipe: PropTypes.shape({
        id: PropTypes.string,
        is_preview: PropTypes.bool,
        book: PropTypes.shape({
          isbn: PropTypes.string,
        }),
      }),
      reference: PropTypes.shape({
        id: PropTypes.string,
        is_preview: PropTypes.bool,
        book: PropTypes.shape({
          isbn: PropTypes.string,
        }),
      }),
      section: PropTypes.shape({
        id: PropTypes.string,
        is_preview: PropTypes.bool,
        book: PropTypes.shape({
          isbn: PropTypes.string,
        }),
      }),
    };

    static defaultProps = {
      clientId: null,
      loading: false,
      checkingAccess: false,
      recipe: {},
      reference: {},
      section: {},
    };

    componentDidMount() {
      const { loading, checkingAccess } = this.props;
      const content = this.getContentIdentifiers(this.props);
      if (content && !loading && !checkingAccess) {
        this.logUserAccess(content);
      }
    }

    componentDidUpdate() {
      const { loading, checkingAccess } = this.props;
      const current = this.getContentIdentifiers(this.props) || {};
      if (this.lastLoggedId !== current.id && !loading && !checkingAccess) {
        this.reset();
        this.logUserAccess(current);
      }
    }

    componentWillUnmount() {
      this.reset();
    }

    getContentIdentifiers(props) {
      let id = null;
      let isbn = null;
      let hash = null;
      let accessType = ACCESS_TYPE_NORMAL;
      let logExtended = true;

      switch (contentType) {
        case CONTENT_TYPES.RECIPE:
          id = props.recipe.id;
          isbn = props.recipe.book.isbn;
          hash = props.recipe.hash;
          accessType = this.props.recipe.is_preview ? ACCESS_TYPE_PAYWALL : ACCESS_TYPE_NORMAL;
          logExtended = accessType === ACCESS_TYPE_NORMAL;
          break;
        case CONTENT_TYPES.SECTION:
          id = props.section.id;
          isbn = props.section.book.isbn;
          hash = props.section.hash;
          accessType = this.props.section.is_preview ? ACCESS_TYPE_PAYWALL : ACCESS_TYPE_NORMAL;
          logExtended = accessType === ACCESS_TYPE_NORMAL;
          break;
        case CONTENT_TYPES.REFERENCE:
          id = props.reference.id;
          isbn = props.reference.book.isbn;
          hash = props.reference.hash;
          accessType = this.props.reference.is_preview ? ACCESS_TYPE_PAYWALL : ACCESS_TYPE_NORMAL;
          logExtended = accessType === ACCESS_TYPE_NORMAL;
          break;
        case CONTENT_TYPES.COLLECTION:
          if (!props.collection) {
            return null;
          }
          id = props.collection.id;
          logExtended = false;
          break;
        default:
          throw new Error(`contentType must be one of ${CONTENT_TYPES}`);
      }
      return { id, isbn, hash, logExtended, accessType, alacarte: !!props.alacarte };
    }

    logUserAccess({ id, isbn, hash, logExtended, accessType, alacarte }) {
      const { clientId } = this.props;
      this.lastLoggedId = id;
      this.waitingToLogInitialAccess = setTimeout(() => {
        this.props.logAccess({
          accessType,
          contentType,
          id,
          isbn,
          contentHash: hash,
          clientId,
          alacarte,
        });
      }, INITIAL_ACCESS_DELAY);
      if (logExtended) {
        this.waitingToLogExtendedAccess = setTimeout(() => {
          this.props.logAccess({
            accessType: ACCESS_TYPE_EXTENDED,
            contentType,
            id,
            isbn,
            contentHash: hash,
            clientId,
            alacarte,
          });
        }, EXTENDED_ACCESS_DELAY);
      }
    }

    reset() {
      if (this.waitingToLogInitialAccess) {
        clearTimeout(this.waitingToLogInitialAccess);
      }
      if (this.waitingToLogExtendedAccess) {
        clearTimeout(this.waitingToLogExtendedAccess);
      }
      this.lastLoggedId = null;
    }

    render() {
      return <WrappedComponent {...this.props} />;
    }
  }

  const mapStateToProps = state => ({
    clientId: state.device.clientId,
  });

  const mapDispatchToProps = {
    logAccess,
  };

  return connect(
    mapStateToProps,
    mapDispatchToProps,
  )(UserAccessLogger);
}

export default withUserAccessLogger;
